import { AutoAwesome, ErrorOutline, TipsAndUpdates } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Alert, Box, Chip, Divider, Stack, Tooltip, Typography } from "@mui/material";
import { Fragment, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { TASK_FIELDS } from "../../../app/constants/config/systemTasks";
import { LoadingProgress } from "../../../components/ui/UiUtils";
import { sectionUiLookBase } from "../../../styled/inlineCssStyles";
import { useAnalyzeDraftTaskMutation, useAnalyzeTaskMutation, useGetTaskAnalysisMutation } from "../../../hooks/ApiHooks";
import { selectTaskFieldValue } from "../slice/taskSlice";

const CONFIDENCE_COLOR = { high: "success", medium: "warning", low: "default" };
const PRIORITY_COLOR = { high: "error", medium: "warning", low: "info" };

// Format a stored ISO timestamp for the "Generated …" header.
const formatTs = (value) => {
  if (!value) return "";
  const d = new Date(value);
  return isNaN(d.getTime()) ? "" : d.toLocaleString();
};

// Rich-text fields are stored as HTML; strip to plain text for gating + payload.
const htmlToText = (value) =>
  typeof value === "string"
    ? value
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/\s+/g, " ")
        .trim()
    : "";

// The backend returns `ai` as a per-provider map ({ claude: {...}, mistral: {...} })
// or a bare { error }. Pick the first provider that produced a result.
const pickResult = (ai) => {
  if (!ai) return { error: "No AI response returned" };
  if (ai.error) return { error: ai.error };
  const ordered = ["claude", "mistral", ...Object.keys(ai)];
  for (const provider of ordered) {
    const entry = ai[provider];
    if (entry && entry.result) {
      return { provider, model: entry.model, latency: entry.latency_ms, result: entry.result };
    }
  }
  const firstKey = Object.keys(ai)[0];
  return { error: (firstKey && ai[firstKey]?.error) || "AI analysis unavailable" };
};

const SectionLabel = ({ children }) => (
  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary", mt: 1.5, mb: 0.5 }}>
    {children}
  </Typography>
);

const AiResultView = ({ ai }) => {
  const picked = useMemo(() => pickResult(ai), [ai]);

  if (picked.error) {
    return (
      <Alert severity="warning" icon={<ErrorOutline fontSize="inherit" />} sx={{ mt: 1 }}>
        {picked.error}
      </Alert>
    );
  }

  const r = picked.result;
  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Diagnostic Suggestions
        </Typography>
        {!!r.confidence && (
          <Chip
            size="small"
            label={`Confidence: ${r.confidence}`}
            color={CONFIDENCE_COLOR[r.confidence] || "default"}
            variant="outlined"
          />
        )}
      </Stack>

      {!!r.summary && (
        <Typography variant="body2" sx={{ mt: 1, color: "text.primary" }}>
          {r.summary}
        </Typography>
      )}

      {Array.isArray(r.likely_causes) && r.likely_causes.length > 0 && (
        <Fragment>
          <SectionLabel>Likely Causes</SectionLabel>
          <Stack component="ul" sx={{ pl: 2, m: 0 }} spacing={0.5}>
            {r.likely_causes.map((c, i) => (
              <li key={i}>
                <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                  {c.cause}
                </Typography>
                {!!c.rationale && (
                  <Typography variant="body2" component="span" sx={{ color: "text.secondary" }}>
                    {" — "}
                    {c.rationale}
                  </Typography>
                )}
              </li>
            ))}
          </Stack>
        </Fragment>
      )}

      {Array.isArray(r.recommended_actions) && r.recommended_actions.length > 0 && (
        <Fragment>
          <SectionLabel>Recommended Actions</SectionLabel>
          <Stack spacing={0.75}>
            {r.recommended_actions.map((a, i) => (
              <Stack key={i} direction="row" alignItems="center" gap={1}>
                <Chip size="small" label={a.priority} color={PRIORITY_COLOR[a.priority] || "default"} sx={{ minWidth: 68 }} />
                <Typography variant="body2">{a.action}</Typography>
              </Stack>
            ))}
          </Stack>
        </Fragment>
      )}

      {Array.isArray(r.likely_parts) && r.likely_parts.length > 0 && (
        <Fragment>
          <SectionLabel>Likely Parts</SectionLabel>
          <Stack component="ul" sx={{ pl: 2, m: 0 }} spacing={0.5}>
            {r.likely_parts.map((p, i) => (
              <li key={i}>
                <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                  {p.part_name}
                  {p.part_id ? ` (${p.part_id})` : ""}
                </Typography>
                {!!p.reason && (
                  <Typography variant="body2" component="span" sx={{ color: "text.secondary" }}>
                    {" — "}
                    {p.reason}
                  </Typography>
                )}
              </li>
            ))}
          </Stack>
        </Fragment>
      )}

      {Array.isArray(r.similar_tasks) && r.similar_tasks.length > 0 && (
        <Fragment>
          <SectionLabel>Similar Past Tasks</SectionLabel>
          <Stack component="ul" sx={{ pl: 2, m: 0 }} spacing={0.5}>
            {r.similar_tasks.map((t, i) => (
              <li key={i}>
                <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                  {t.task_id}
                </Typography>
                {!!t.relevance && (
                  <Typography variant="body2" component="span" sx={{ color: "text.secondary" }}>
                    {" — "}
                    {t.relevance}
                  </Typography>
                )}
              </li>
            ))}
          </Stack>
        </Fragment>
      )}

      {!!htmlToText(r.equipment_notes) && (
        <Fragment>
          <SectionLabel>Equipment Notes</SectionLabel>
          <Typography variant="body2" sx={{ color: "text.primary" }}>
            {r.equipment_notes}
          </Typography>
        </Fragment>
      )}

      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        AI-generated — verify before acting.
        {picked.model ? ` · ${picked.model}` : ""}
        {typeof picked.latency === "number" ? ` · ${picked.latency} ms` : ""}
      </Typography>
    </Box>
  );
};

// Shared button + result surface. `disabledReason`, when set, disables the
// button and is shown as a tooltip. `generatedAt`/`stale` drive the saved-result
// header; `isFetching` is the (free) load of a stored analysis, distinct from
// `isLoading` which is the paid generate/regenerate call.
const AiAnalysisSurface = ({
  onAnalyze,
  buttonLabel = "AI Analyze",
  isLoading,
  isFetching,
  isError,
  error,
  ai,
  generatedAt,
  stale,
  disabledReason,
}) => {
  const button = (
    <LoadingButton
      loading={isLoading}
      variant="outlined"
      color="primary"
      startIcon={<AutoAwesome />}
      onClick={onAnalyze}
      disabled={!!disabledReason}
    >
      {buttonLabel}
    </LoadingButton>
  );

  return (
    <Box sx={{ ...sectionUiLookBase, p: 2, my: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
        <Stack direction="row" alignItems="center" gap={1}>
          <TipsAndUpdates color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            AI Analysis
          </Typography>
        </Stack>
        {disabledReason ? (
          <Tooltip title={disabledReason}>
            <span>{button}</span>
          </Tooltip>
        ) : (
          button
        )}
      </Stack>

      {!!generatedAt && (
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Generated {formatTs(generatedAt)}
          </Typography>
          {!!stale && (
            <Chip size="small" color="warning" variant="outlined" label="Task changed since — regenerate" />
          )}
        </Stack>
      )}

      {isLoading ? (
        <Stack alignItems="center" sx={{ py: 3 }}>
          <LoadingProgress size={28} />
          <Typography variant="caption" sx={{ mt: 1, color: "text.secondary" }}>
            Gathering history and reasoning over it…
          </Typography>
        </Stack>
      ) : isError ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error?.data?.message || "AI analysis request failed."}
        </Alert>
      ) : isFetching && !ai ? (
        <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
          Loading saved analysis…
        </Typography>
      ) : (
        ai && <AiResultView ai={ai} />
      )}
    </Box>
  );
};

/* View Task page — analyse an existing, saved task by id.
   Loads any previously saved analysis on open (free); the paid generate only
   runs on an explicit AI Analyze / Regenerate click. */
export const AiAnalyzeExistingTask = ({ taskId }) => {
  const { triggerMutation: fetchStored, isLoading: isFetching, data: storedData } = useGetTaskAnalysisMutation();
  const {
    triggerMutation: generate,
    isLoading: isGenerating,
    isError,
    error,
    data: generatedData,
  } = useAnalyzeTaskMutation();

  // Pull the saved analysis once the task id is available.
  useEffect(() => {
    if (taskId) fetchStored(taskId);
  }, [taskId, fetchStored]);

  // A freshly generated result takes precedence over the stored one.
  const fresh = generatedData?.data;
  const stored = storedData?.data;
  const ai = fresh?.ai ?? stored?.ai ?? null;
  const generatedAt = fresh?.generated_at ?? stored?.generated_at ?? null;
  const stale = fresh ? false : Boolean(stored?.stale);

  return (
    <AiAnalysisSurface
      onAnalyze={() => generate(taskId)}
      buttonLabel={ai ? "Regenerate" : "AI Analyze"}
      isLoading={isGenerating}
      isFetching={isFetching}
      isError={isError}
      error={error}
      ai={ai}
      generatedAt={generatedAt}
      stale={stale}
      disabledReason={!taskId ? "Task is still loading" : ""}
    />
  );
};

/* Add Task page — analyse the unsaved draft from the form fields.
   Enabled once a store and a problem description are filled. */
export const AiAnalyzeDraftTask = () => {
  const storeId = useSelector((state) => selectTaskFieldValue(state, TASK_FIELDS.STORE.ID));
  const equipmentIds = useSelector((state) => selectTaskFieldValue(state, TASK_FIELDS.EQUIPMENT.ID));
  const taskType = useSelector((state) => selectTaskFieldValue(state, TASK_FIELDS.TYPE.ID));
  const title = useSelector((state) => selectTaskFieldValue(state, TASK_FIELDS.TITLE.ID));
  const description = useSelector((state) => selectTaskFieldValue(state, TASK_FIELDS.DESCRIPTION.ID));
  const problem = useSelector((state) => selectTaskFieldValue(state, TASK_FIELDS.PROBLEM.ID));
  const cause = useSelector((state) => selectTaskFieldValue(state, TASK_FIELDS.CAUSE.ID));

  const { triggerMutation, isLoading, isError, error, data } = useAnalyzeDraftTaskMutation();

  const problemText = htmlToText(problem);
  const disabledReason = !storeId
    ? "Select a store first"
    : !problemText
      ? "Fill in the problem description first"
      : "";

  const onAnalyze = () =>
    triggerMutation({
      store_id: storeId,
      equipment_ids: Array.isArray(equipmentIds) ? equipmentIds : equipmentIds ? [equipmentIds] : [],
      task_type: taskType || undefined,
      task_title: htmlToText(title) || undefined,
      task_desc: htmlToText(description) || undefined,
      task_prob: problemText,
      task_prob_cause: htmlToText(cause) || undefined,
    });

  return (
    <AiAnalysisSurface
      onAnalyze={onAnalyze}
      isLoading={isLoading}
      isError={isError}
      error={error}
      ai={data?.data?.ai}
      disabledReason={disabledReason}
    />
  );
};
